import { ChainDefaultId, ChainId, Currency, ERC20Token } from "@pancakeswap/sdk";
import { FEE_TIERS, Pool } from "@pancakeswap/v3-sdk";
import { gql } from "graphql-request";
import { useMemo } from "react";
import useSWRImmutable from "swr/immutable";
import { getPositionManagerContract } from "utils/contractHelpers";
import { statsClients, v3Clients } from "utils/graphql";
import { publicClient } from "utils/wagmi";
import { Address, encodePacked, keccak256 } from "viem";

interface PoolFields {
    id: string
    version: string
    feeTier: string
    liquidity: string
    reserve0: string
    reserve1: string
    reserve2: string
    tick: string
    tickHigh1d: string
    tickLow1d: string
    volume: string
    volume1d: string
    volume7d: string
    fee1d: string
    fee7d: string
    apr1d: string
    apr7d: string
    token0: {
      id: string
      symbol: string
      name: string
      decimals: number
    }
    token1: {
      id: string
      symbol: string
      name: string
      decimals: number
    }
    token2?: {
      id: string
      symbol: string
      name: string
      decimals: number
    }
}

export type PoolData = {
    address: `0x${string}`;
    version: string
    feeTier?: number
    liquidity: number
    reserve0: number
    reserve1: number
    reserve2?: number
    tick: number
    tickHigh24H: number
    tickLow24H: number
    volume: number
    volume1d: number
    volume7d: number
    fee1d: number
    fee7d: number
    apr1d: number
    apr7d: number
    farm?: boolean
    token0: ERC20Token
    token1: ERC20Token
    token2?: ERC20Token
}


export function useProtocolStats() {
    const { data, status, mutate } = useSWRImmutable(
        ['info/protocol/stats'],
        async () => {
            const { variables } = await statsClients[ChainId.PULSE].request<{ variables: {id: string, value: string}[] }>(gql`
                query {
                    variables {
                        id
                        value
                    }
                }
            `)
            return variables.reduce((map, v) => ({ ...map, [v.id]: v.value }), {})
        },
        {
            keepPreviousData: true,
            refreshInterval: 60_000
        },
    )
    return { data, status, reload: () => mutate(undefined, { revalidate: true }) }
}

export function useAllPools(version: string, query: string = '', sortField: string = 'liquidity', sortDesc: boolean = true, page: number = 0, size: number = 20) {
    const positionManager = getPositionManagerContract()
    const { data, status, mutate } = useSWRImmutable(
        ['info/pools/all', version, query, sortField, sortDesc ? 'desc' : 'asc', page, size],
        async () => {
            const where = `
                where: {
                    and: [
                    ${ version ? `{ version: $version }` : '{ version_not: "v2" }' },
                    ${
                        query 
                        ? `{or: [
                            { id: $query },
                            { token0_: { id: $query } },
                            { token0_: { name_contains_nocase: $query } },
                            { token0_: { symbol_contains_nocase: $query } },
                            { token1_: { id: $query } },
                            { token1_: { name_contains_nocase: $query } },
                            { token1_: { symbol_contains_nocase: $query } },
                            { token2_: { id: $query } },
                            { token2_: { name_contains_nocase: $query } },
                            { token2_: { symbol_contains_nocase: $query } },
                        ]}` : ''
                    }]
                }
            `
            const { pools } = await statsClients[ChainId.PULSE].request<{ pools: PoolFields[] }>(gql`
                query ($version: String, $query: String) {
                    pools: poolDatas(${where}, orderBy: ${sortField}, orderDirection: ${sortDesc ? 'desc' : 'asc'}, first: ${size}, skip: ${page * size}) {
                        id
                        version 
                        feeTier
                        liquidity
                        reserve0
                        reserve1
                        reserve2
                        tick
                        tickHigh1d
                        tickLow1d
                        volume
                        volume1d
                        volume7d
                        fee1d
                        fee7d
                        apr1d
                        apr7d
                        token0 {
                            id
                            symbol
                            name
                            decimals
                        }
                        token1 {
                            id
                            symbol
                            name
                            decimals
                        }
                        token2 {
                            id
                            symbol
                            name
                            decimals
                        }
                    }
                }
            `, { query, version })
            const farmsResult = await publicClient({ chainId: ChainId.PULSE }).multicall({
                contracts: pools.map(pool => ({
                    abi: positionManager.abi,
                    address: positionManager.address,
                    functionName: 'farms',
                    args: [keccak256(encodePacked(['address', 'address'], [pool.token0.id as Address, pool.token1.id as Address]))] as const,
                  } as const
                )),
                allowFailure: true,
            })
            return pools.map((pool, i) => ({
                address: pool.id,
                version: pool.version,
                feeTier: Number(pool.feeTier),
                liquidity: Number(pool.liquidity),
                reserve0: Number(pool.reserve0),
                reserve1: Number(pool.reserve1),
                reserve2: pool.reserve2 ? Number(pool.reserve2) : undefined,
                tick: Number(pool.tick ?? 0),
                tickHigh24H: Number(pool.tickHigh1d ?? 0),
                tickLow24H: Number(pool.tickLow1d ?? 0),
                volume: Number(pool.volume),
                volume1d: Number(pool.volume1d),
                volume7d: Number(pool.volume7d),
                fee1d: Number(pool.fee1d),
                fee7d: Number(pool.fee7d),
                apr1d: Number(pool.apr1d),
                apr7d: Number(pool.apr7d),
                farm: farmsResult?.[i]?.result === true,
                token0: new ERC20Token(ChainDefaultId, pool.token0.id as `0x${string}`, Number(pool.token0.decimals), pool.token0.symbol, pool.token0.name),
                token1: new ERC20Token(ChainDefaultId, pool.token1.id as `0x${string}`, Number(pool.token1.decimals), pool.token1.symbol, pool.token1.name),
                token2: pool.token2 ? new ERC20Token(ChainDefaultId, pool.token2.id as `0x${string}`, Number(pool.token2.decimals), pool.token2.symbol, pool.token2.name) : undefined,
            } as PoolData))
        },
        {
            keepPreviousData: true,
            refreshInterval: 60_000
        },
    )
    return { data, status, reload: () => mutate(undefined, { revalidate: true }) }
}

export function usePools(addresses: string[]) {
    const positionManager = getPositionManagerContract()
    const { data, status, mutate: reload } = useSWRImmutable(
        ['info/pools/addresses', addresses],
        async () => {
            const { pools } = await statsClients[ChainId.PULSE].request<{ pools: PoolFields[] }>(gql`
                query ($addresses: [String!]!) {
                    pools: poolDatas(where: { id_in: $addresses }) {
                        id
                        version
                        feeTier
                        liquidity
                        reserve0
                        reserve1
                        reserve2
                        tick
                        tickHigh1d
                        tickLow1d
                        volume
                        volume1d
                        volume7d
                        fee1d
                        fee7d
                        apr1d
                        apr7d
                        token0 {
                            id
                            symbol
                            name
                            decimals
                        }
                        token1 {
                            id
                            symbol
                            name
                            decimals
                        }
                        token2 {
                            id
                            symbol
                            name
                            decimals
                        }
                    }
                }
            `, { addresses })
            const farmsResult = await publicClient({ chainId: ChainId.PULSE }).multicall({
                contracts: pools.map(pool => ({
                    abi: positionManager.abi,
                    address: positionManager.address,
                    functionName: 'farms',
                    args: [keccak256(encodePacked(['address', 'address'], [pool.token0.id as Address, pool.token1.id as Address]))] as const,
                  } as const
                )),
                allowFailure: true,
            })
            return pools.map((pool, i) => ({
                address: pool.id,
                version: pool.version,
                feeTier: Number(pool.feeTier),
                liquidity: Number(pool.liquidity),
                reserve0: Number(pool.reserve0),
                reserve1: Number(pool.reserve1),
                reserve2: pool.reserve2 ? Number(pool.reserve2) : undefined,
                tick: Number(pool.tick ?? 0),
                tickHigh24H: Number(pool.tickHigh1d ?? 0),
                tickLow24H: Number(pool.tickLow1d ?? 0),
                volume: Number(pool.volume),
                volume1d: Number(pool.volume1d),
                volume7d: Number(pool.volume7d),
                fee1d: Number(pool.fee1d),
                fee7d: Number(pool.fee7d),
                apr1d: Number(pool.apr1d),
                apr7d: Number(pool.apr7d),
                farm: farmsResult?.[i]?.result === true,
                token0: new ERC20Token(ChainDefaultId, pool.token0.id as `0x${string}`, Number(pool.token0.decimals), pool.token0.symbol, pool.token0.name),
                token1: new ERC20Token(ChainDefaultId, pool.token1.id as `0x${string}`, Number(pool.token1.decimals), pool.token1.symbol, pool.token1.name),
                token2: pool.token2 ? new ERC20Token(ChainDefaultId, pool.token2.id as `0x${string}`, Number(pool.token2.decimals), pool.token2.symbol, pool.token2.name) : undefined,
            } as PoolData))
        },
        {
            keepPreviousData: true,
            refreshInterval: 60_000
        },
    )
    return { data, status, reload }
}

export function usePoolTiers(currencyA: Currency, currencyB: Currency) {
    const feeTiers = FEE_TIERS.reduce((_map, _fee) => 
        ({ ..._map, [_fee]: Pool.getAddress(currencyA.wrapped, currencyB.wrapped, _fee).toLowerCase() }),
        {}
    )
    const { data, status } = useSWRImmutable(
        ['info/pools/tiers', currencyA, currencyB],
        async () => {
            const { pools } = await statsClients[ChainId.PULSE].request<{ pools: PoolFields[] }>(gql`
                query ($addresses: [String!]!) {
                    pools: poolDatas(where: { id_in: $addresses, version: "v3" }) {
                        id
                        version
                        feeTier
                        liquidity
                        reserve0
                        reserve1
                        tick
                        tickHigh1d
                        tickLow1d
                        volume
                        volume1d
                        volume7d
                        fee1d
                        fee7d
                        apr1d
                        apr7d
                        token0 {
                            id
                            symbol
                            name
                            decimals
                        }
                        token1 {
                            id
                            symbol
                            name
                            decimals
                        }
                    }
                }
            `, { addresses: Object.values(feeTiers) })
            const poolDatas = pools.map(pool => ({
                address: pool.id,
                version: pool.version,
                feeTier: Number(pool.feeTier),
                liquidity: Number(pool.liquidity),
                reserve0: Number(pool.reserve0),
                reserve1: Number(pool.reserve1),
                tick: Number(pool.tick ?? 0),
                tickHigh24H: Number(pool.tickHigh1d ?? 0),
                tickLow24H: Number(pool.tickLow1d ?? 0),
                volume: Number(pool.volume),
                volume1d: Number(pool.volume1d),
                volume7d: Number(pool.volume7d),
                fee1d: Number(pool.fee1d),
                fee7d: Number(pool.fee7d),
                apr1d: Number(pool.apr1d),
                apr7d: Number(pool.apr7d),
                token0: new ERC20Token(ChainDefaultId, pool.token0.id as `0x${string}`, Number(pool.token0.decimals), pool.token0.symbol, pool.token0.name),
                token1: new ERC20Token(ChainDefaultId, pool.token1.id as `0x${string}`, Number(pool.token1.decimals), pool.token1.symbol, pool.token1.name),
            } as PoolData))
            const { distributions } = await v3Clients[ChainId.PULSE].request<{ distributions: any[] }>(gql`
                query ($addresses: [String!]!) {
                    distributions: pools(where: { id_in: $addresses }) {
                        id
                        liquidity
                    }
                }
            `, { addresses: Object.values(feeTiers) })
            const total = distributions.reduce((_tvl, _pool) => _tvl + Number(_pool.liquidity), 0)
            const max = Math.max(...distributions.map((_pool) => _pool.liquidity * 100 / total))
            const percents = distributions.reduce((_dist, _pool) => ({ ..._dist, [_pool.id]: _pool.liquidity * 100 / total }), {})
            const poolDatasWithPercent = poolDatas.map((_pool) => ({ ..._pool, distribution: percents[_pool.address], mostPick: percents[_pool.address]===max }))
            return Object.fromEntries(Object.entries(feeTiers).map(([k, v]) => [k, poolDatasWithPercent.find(p => p.address === v)]))
        },
        {
            keepPreviousData: true,
            refreshInterval: 60_000
        },
    )
    return useMemo(() => {
        return { data, status }
    }, [data, status])
}

export function usePricesIn24H(poolAddress: string) {
    const { data, status } = useSWRImmutable(
        ['info/pool/v3/prices', poolAddress],
        async () => {
            const { pools } = await v3Clients[ChainDefaultId].request<{ pools: any[] }>(gql`
                query($address: ID!) {
                    pools:poolDayDatas(where: { pool: $address }, orderBy: date, orderDirection: desc, first: 1) {
                        high, low
                    }
                }
            `, {
                address: poolAddress
            })
            if (pools?.[0])
                return [pools[0].low, pools[0].high]
            return undefined
        },
        {
            keepPreviousData: true,
            refreshInterval: 60_000
        },
    )
    return useMemo(() => {
        return { data, status }
    }, [data, status])
}